import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { OrgProvider, useOrg } from './contexts/OrgContext';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import Login from './pages/Login';
import Register from './pages/Register';
import Setup from './pages/Setup';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import ChecklistPage from './pages/ChecklistPage';
import Submissions from './pages/Submissions';
import SubmissionDetail from './pages/SubmissionDetail';
import Admin from './pages/Admin';
import AdminAssets from './pages/AdminAssets';
import AdminUsers from './pages/AdminUsers';
import AdminTemplates from './pages/AdminTemplates';
import { getQueue, removeFromQueue, getQueueCount } from './utils/offlineQueue';
import { supabase } from './supabaseClient';

function OfflineSyncManager({ onQueueChange }) {
  useEffect(() => {
    let isSyncing = false;

    async function syncQueue() {
      if (isSyncing || !navigator.onLine) return;
      const queue = getQueue();
      if (queue.length === 0) return;

      isSyncing = true;
      onQueueChange({ count: queue.length, syncing: true });

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { isSyncing = false; return; }

      for (let i = queue.length - 1; i >= 0; i--) {
        const item = { ...queue[i] };
        delete item.queued_at;
        const { error } = await supabase.from('submissions').insert(item);
        if (!error) removeFromQueue(i);
      }

      isSyncing = false;
      onQueueChange({ count: getQueueCount(), syncing: false });
    }

    syncQueue();
    window.addEventListener('online', syncQueue);
    return () => window.removeEventListener('online', syncQueue);
  }, [onQueueChange]);

  return null;
}

function SetupGuard() {
  const { user, loading: authLoading } = useAuth();
  const { membership, loading: orgLoading } = useOrg();

  if (authLoading || orgLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-orange-500 text-lg font-semibold animate-pulse">GroundCheck</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (membership) return <Navigate to="/dashboard" replace />;
  return <Setup />;
}

export default function App() {
  const [syncState, setSyncState] = useState({ count: getQueueCount(), syncing: false });

  return (
    <AuthProvider>
      <OrgProvider>
        <OfflineSyncManager onQueueChange={setSyncState} />
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/setup" element={<SetupGuard />} />

            <Route element={<ProtectedRoute />}>
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route
                path="/dashboard"
                element={<Dashboard queueCount={syncState.count} syncing={syncState.syncing} />}
              />
              <Route path="/checklist/:type" element={<ChecklistPage />} />
              <Route path="/submissions" element={<Submissions />} />
              <Route path="/submissions/:id" element={<SubmissionDetail />} />

              <Route element={<AdminRoute />}>
                <Route path="/admin" element={<Admin />} />
                <Route path="/admin/assets" element={<AdminAssets />} />
                <Route path="/admin/users" element={<AdminUsers />} />
                <Route path="/admin/templates" element={<AdminTemplates />} />
              </Route>
            </Route>

            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </BrowserRouter>
      </OrgProvider>
    </AuthProvider>
  );
}
